import {DataTestAttrs, getTestProps} from '@benchling/js-test-utils';
import {DatePickerInput, DatesRangeValue} from '@mantine/dates';
import React from 'react';

import {RangeFieldSettings} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import {GetPipelineConfigSpecType} from 'pipelines/StepShapeUtils';
import {JSONObject} from 'util/Types';

import {DATE_DISPLAY_FORMAT, formatToISODate, parseISODate} from './DateEditorUtils';

export interface DateRangeEditorProps {
  config: JSONObject;
  configShape: GetPipelineConfigSpecType<'shape'>;
  onChangeConfig: (config: JSONObject) => void;
  fieldSettings: RangeFieldSettings;
  disabled?: boolean;
  dataTestAttrs?: DataTestAttrs;
}

export default function DateRangeEditor({
  config,
  configShape,
  onChangeConfig,
  fieldSettings,
  disabled,
  dataTestAttrs = {},
}: DateRangeEditorProps): JSX.Element {
  const startDateKey = fieldSettings.startKey;
  const endDateKey = fieldSettings.endKey;

  const handleChange = (range: DatesRangeValue): void => {
    const [startDate, endDate] = range;
    onChangeConfig({
      [startDateKey]: formatToISODate(startDate),
      [endDateKey]: formatToISODate(endDate),
    });
  };

  const startDateField = configShape.fields[startDateKey];
  const endDateField = configShape.fields[endDateKey];
  const isRequired =
    (startDateField != null && startDateField.required) || (endDateField != null && endDateField.required);

  const value: DatesRangeValue = [
    parseISODate(config[startDateKey] as string | null),
    parseISODate(config[endDateKey] as string | null),
  ];

  return (
    <DatePickerInput
      type="range"
      disabled={disabled}
      placeholder="Select date range"
      value={value}
      onChange={handleChange}
      valueFormat={DATE_DISPLAY_FORMAT}
      clearable={!isRequired}
      styles={{
        input: {
          flexGrow: 1,
          borderRadius: '4px',
        },
      }}
      {...getTestProps(dataTestAttrs)}
    />
  );
}
